import {
  getAllNotes,
  getMeta,
  getOutboxOperations,
  isNativeDatabase,
  putNote,
  setMeta,
} from "./db.js";
import { generateVaultKey, normalizeVaultKey } from "./crypto.js";
import { isNativeApp } from "./native.js";
import {
  checkForAppUpdate,
  getCurrentAppVersion,
  installAppUpdate,
} from "./update.js";
import {
  queueNote,
  synchronize,
  SyncConfigurationError,
  SyncEncryptionError,
} from "./sync.js";
import { drainSyncSession } from "./sync-session.js";
import {
  clampSwipeOffset,
  shouldOpenSwipe,
  swipeAxis,
  SWIPE_ACTION_WIDTH,
} from "./swipe.js";

const DEFAULT_SYNC_URL = "https://notes.cctc.ai";
const LOCAL_SAVE_DELAY = 260;
const EDIT_SYNC_IDLE_DELAY = 2_200;
const PERIODIC_SYNC_INTERVAL = 30_000;
const MOBILE_HISTORY_STATE_KEY = "lightBoatMobileView";
const MOBILE_LIST_VIEW = "list";
const MOBILE_EDITOR_VIEW = "editor";

const elements = {
  newNote: document.querySelector("#new-note"),
  emptyNewNote: document.querySelector("#empty-new-note"),
  search: document.querySelector("#search"),
  filterTabs: [...document.querySelectorAll(".filter-tab")],
  listTitle: document.querySelector("#list-title"),
  noteCount: document.querySelector("#note-count"),
  notesList: document.querySelector("#notes-list"),
  syncButton: document.querySelector("#sync-button"),
  syncLabel: document.querySelector("#sync-label"),
  settingsButton: document.querySelector("#settings-button"),
  settingsOverlay: document.querySelector("#settings-overlay"),
  closeSettings: document.querySelector("#close-settings"),
  cancelSettings: document.querySelector("#cancel-settings"),
  saveSettings: document.querySelector("#save-settings"),
  settingsError: document.querySelector("#settings-error"),
  serverUrl: document.querySelector("#server-url"),
  serverToken: document.querySelector("#server-token"),
  vaultKey: document.querySelector("#vault-key"),
  generateVaultKey: document.querySelector("#generate-vault-key"),
  copyVaultKey: document.querySelector("#copy-vault-key"),
  revealVaultKey: document.querySelector("#reveal-vault-key"),
  currentVersion: document.querySelector("#current-version"),
  updateStatus: document.querySelector("#update-status"),
  checkUpdate: document.querySelector("#check-update"),
  editorEmpty: document.querySelector("#editor-empty"),
  editorView: document.querySelector("#editor-view"),
  editor: document.querySelector("#editor"),
  editedTime: document.querySelector("#edited-time"),
  wordCount: document.querySelector("#word-count"),
  backButton: document.querySelector("#back-button"),
  pinNote: document.querySelector("#pin-note"),
  deleteNote: document.querySelector("#delete-note"),
  toast: document.querySelector("#toast"),
};

const state = {
  notes: [],
  activeId: null,
  deviceId: null,
  view: "notes",
  search: "",
  saveTimer: null,
  syncTimer: null,
  toastTimer: null,
  cloudReady: false,
  lastInputAt: 0,
  editGeneration: 0,
  syncInProgress: false,
  syncAgain: false,
  openSwipeId: null,
  availableUpdate: null,
  updateChecking: false,
};

const mobileQuery = window.matchMedia("(max-width: 760px)");

function mobileHistoryState(view, noteId = null) {
  return {
    ...(history.state && typeof history.state === "object" ? history.state : {}),
    [MOBILE_HISTORY_STATE_KEY]: view,
    lightBoatNoteId: noteId,
  };
}

function listHistoryUrl() {
  return `${location.pathname}${location.search}`;
}

function editorHistoryUrl(noteId) {
  return `${listHistoryUrl()}#note=${encodeURIComponent(noteId)}`;
}

function prepareMobileHistory() {
  history.replaceState(mobileHistoryState(MOBILE_LIST_VIEW), "", listHistoryUrl());
}

function openMobileEditor(noteId) {
  if (!mobileQuery.matches) return;

  if (history.state?.[MOBILE_HISTORY_STATE_KEY] === MOBILE_EDITOR_VIEW) {
    history.replaceState(mobileHistoryState(MOBILE_EDITOR_VIEW, noteId), "", editorHistoryUrl(noteId));
  } else {
    history.pushState(mobileHistoryState(MOBILE_EDITOR_VIEW, noteId), "", editorHistoryUrl(noteId));
  }
  document.body.classList.add("editor-open");
}

function closeMobileEditor() {
  document.body.classList.remove("editor-open");
  elements.editor.blur();
}

function returnToMobileList() {
  closeMobileEditor();
  if (mobileQuery.matches && history.state?.[MOBILE_HISTORY_STATE_KEY] === MOBILE_EDITOR_VIEW) {
    history.back();
  }
}

prepareMobileHistory();

function activeNote() {
  return state.notes.find((note) => note.id === state.activeId) || null;
}

function titleFor(note) {
  const firstLine = note.body.split("\n").find((line) => line.trim());
  return firstLine?.trim() || "新笔记";
}

function previewFor(note) {
  const meaningful = note.body
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  return meaningful.slice(1).join(" ");
}

function formatRelative(timestamp) {
  const difference = timestamp - Date.now();
  const absolute = Math.abs(difference);
  const formatter = new Intl.RelativeTimeFormat("zh-CN", { numeric: "auto" });

  if (absolute < 60_000) return "刚刚";
  if (absolute < 3_600_000) return formatter.format(Math.round(difference / 60_000), "minute");
  if (absolute < 86_400_000) return formatter.format(Math.round(difference / 3_600_000), "hour");
  if (absolute < 604_800_000) return formatter.format(Math.round(difference / 86_400_000), "day");
  return new Intl.DateTimeFormat("zh-CN", { month: "short", day: "numeric" }).format(timestamp);
}

function filteredNotes() {
  const query = state.search.trim().toLocaleLowerCase("zh-CN");
  return state.notes
    .filter((note) => (state.view === "trash" ? note.deletedAt : !note.deletedAt))
    .filter((note) => !query || note.body.toLocaleLowerCase("zh-CN").includes(query))
    .sort((left, right) => {
      if (state.view === "notes" && left.pinned !== right.pinned) return left.pinned ? -1 : 1;
      return right.updatedAt - left.updatedAt;
    });
}

function renderList() {
  const notes = filteredNotes();
  elements.notesList.replaceChildren();
  elements.noteCount.textContent = `${notes.length} 篇`;
  elements.listTitle.textContent = state.search ? "搜索结果" : state.view === "trash" ? "可恢复" : "最近";

  if (!notes.length) {
    const empty = document.createElement("div");
    empty.className = "list-empty";
    empty.textContent = state.search
      ? "没有找到包含这些文字的笔记。"
      : state.view === "trash"
        ? "最近删除里是空的。"
        : "还没有笔记。点右上角开始写第一篇。";
    elements.notesList.append(empty);
    return;
  }

  for (const note of notes) {
    const row = document.createElement("div");
    row.className = `note-row${note.id === state.openSwipeId ? " is-swiped" : ""}`;
    row.setAttribute("role", "listitem");
    row.dataset.noteId = note.id;

    const swipeAction = document.createElement("button");
    swipeAction.type = "button";
    swipeAction.className = `note-swipe-action${note.deletedAt ? " restore" : ""}`;
    swipeAction.textContent = note.deletedAt ? "恢复" : "删除";
    swipeAction.setAttribute("aria-label", note.deletedAt ? `恢复“${titleFor(note)}”` : `删除“${titleFor(note)}”`);
    swipeAction.addEventListener("click", (event) => {
      event.stopPropagation();
      toggleDeleted(note.id);
    });

    const button = document.createElement("button");
    button.type = "button";
    button.className = `note-card${note.id === state.activeId ? " is-active" : ""}`;
    button.dataset.noteId = note.id;

    const title = document.createElement("span");
    title.className = "note-title";
    if (note.pinned && state.view === "notes") {
      const pin = document.createElement("span");
      pin.className = "pin-dot";
      pin.setAttribute("aria-label", "已置顶");
      title.append(pin);
    }
    title.append(document.createTextNode(titleFor(note)));

    const date = document.createElement("time");
    date.className = "note-date";
    date.dateTime = new Date(note.updatedAt).toISOString();
    date.textContent = formatRelative(note.updatedAt);

    const preview = document.createElement("span");
    preview.className = "note-preview";
    preview.textContent = previewFor(note);
    preview.hidden = !preview.textContent;

    button.append(title, date, preview);
    installSwipeGesture(row, button, note.id);

    const quickAction = document.createElement("button");
    quickAction.type = "button";
    quickAction.className = `note-quick-action${note.deletedAt ? " restore" : ""}`;
    quickAction.setAttribute("aria-label", note.deletedAt ? "恢复笔记" : "移到最近删除");
    quickAction.title = note.deletedAt ? "恢复笔记" : "移到最近删除";
    quickAction.innerHTML = note.deletedAt
      ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12a8 8 0 1 0 2.34-5.66L4 8.68M4 4v4.68h4.68" /></svg>'
      : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" /></svg>';
    quickAction.addEventListener("click", (event) => {
      event.stopPropagation();
      toggleDeleted(note.id);
    });

    row.append(swipeAction, button, quickAction);
    elements.notesList.append(row);
  }
}

function resetSwipeRow(row) {
  row.classList.remove("is-swiped", "is-swipe-dragging");
  row.style.removeProperty("--swipe-offset");
}

function closeOpenSwipe(exceptId = null) {
  for (const row of elements.notesList.querySelectorAll(".note-row")) {
    if (row.dataset.noteId !== exceptId) resetSwipeRow(row);
  }
  if (state.openSwipeId !== exceptId) state.openSwipeId = null;
}

function setSwipeOpen(row, noteId, open) {
  if (open) closeOpenSwipe(noteId);
  resetSwipeRow(row);
  row.classList.toggle("is-swiped", open);
  state.openSwipeId = open ? noteId : state.openSwipeId === noteId ? null : state.openSwipeId;
}

function installSwipeGesture(row, card, noteId) {
  let startX = 0;
  let startY = 0;
  let baseOffset = 0;
  let currentOffset = 0;
  let activePointerId = null;
  let gestureAxis = null;
  let ignoreClickUntil = 0;

  const finish = (event) => {
    if (activePointerId == null || (event?.pointerId != null && event.pointerId !== activePointerId)) return;
    const wasOpen = baseOffset < 0;
    const horizontalGesture = gestureAxis === "horizontal";
    const cancelled = event?.type === "pointercancel";
    const open = horizontalGesture
      ? shouldOpenSwipe(currentOffset, cancelled, wasOpen)
      : wasOpen;

    if (card.hasPointerCapture?.(activePointerId)) card.releasePointerCapture(activePointerId);
    resetSwipeRow(row);
    setSwipeOpen(row, noteId, open);

    if (horizontalGesture) ignoreClickUntil = performance.now() + 450;
    activePointerId = null;
    gestureAxis = null;
  };

  card.addEventListener("pointerdown", (event) => {
    if (!event.isPrimary || (event.pointerType === "mouse" && event.button !== 0)) return;
    const wasOpen = row.classList.contains("is-swiped");
    if (!wasOpen) closeOpenSwipe(noteId);
    startX = event.clientX;
    startY = event.clientY;
    baseOffset = wasOpen ? -SWIPE_ACTION_WIDTH : 0;
    currentOffset = baseOffset;
    activePointerId = event.pointerId;
    gestureAxis = null;
  });

  card.addEventListener("pointermove", (event) => {
    if (!event.isPrimary || event.pointerId !== activePointerId) return;
    const deltaX = event.clientX - startX;
    const deltaY = event.clientY - startY;

    if (!gestureAxis) {
      gestureAxis = swipeAxis(deltaX, deltaY);
      if (!gestureAxis) return;
      if (gestureAxis === "vertical") {
        activePointerId = null;
        return;
      }
      closeOpenSwipe(noteId);
      row.classList.add("is-swipe-dragging");
      card.setPointerCapture?.(event.pointerId);
    }

    currentOffset = clampSwipeOffset(baseOffset + deltaX);
    row.style.setProperty("--swipe-offset", `${currentOffset}px`);
    event.preventDefault();
  });

  card.addEventListener("pointerup", finish);
  card.addEventListener("pointercancel", finish);
  card.addEventListener("click", (event) => {
    if (performance.now() < ignoreClickUntil) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (row.classList.contains("is-swiped")) {
      setSwipeOpen(row, noteId, false);
      return;
    }
    closeOpenSwipe();
    selectNote(noteId, true);
  });
}

function renderEditor({ preserveCaret = false } = {}) {
  const note = activeNote();
  elements.editorEmpty.hidden = Boolean(note);
  elements.editorView.hidden = !note;

  if (!note) {
    closeMobileEditor();
    return;
  }

  if (!preserveCaret && elements.editor.value !== note.body) {
    elements.editor.value = note.body;
  }
  elements.editedTime.textContent = `${formatRelative(note.updatedAt)}修改`;
  elements.wordCount.textContent = `${[...note.body].length} 字`;
  elements.pinNote.classList.toggle("is-active", Boolean(note.pinned));
  elements.pinNote.setAttribute("aria-label", note.pinned ? "取消置顶" : "置顶");
  elements.pinNote.title = note.pinned ? "取消置顶" : "置顶";

  const inTrash = Boolean(note.deletedAt);
  elements.pinNote.hidden = inTrash;
  elements.deleteNote.setAttribute("aria-label", inTrash ? "恢复笔记" : "移到最近删除");
  elements.deleteNote.title = inTrash ? "恢复笔记" : "移到最近删除";
  elements.deleteNote.innerHTML = inTrash
    ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12a8 8 0 1 0 2.34-5.66L4 8.68M4 4v4.68h4.68" /></svg>'
    : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" /></svg>';
}

function selectNote(noteId, openOnMobile = false) {
  state.activeId = noteId;
  renderList();
  renderEditor();
  if (openOnMobile) openMobileEditor(noteId);
  requestAnimationFrame(() => elements.editor.focus({ preventScroll: true }));
}

function setSyncStatus(status, label) {
  elements.syncButton.dataset.state = status;
  elements.syncLabel.textContent = label;
}

function setLocalSaveStatus(label = "本机已保存") {
  setSyncStatus(navigator.onLine ? "local" : "offline", navigator.onLine ? label : "离线，已保存本机");
}

function showToast(message) {
  clearTimeout(state.toastTimer);
  elements.toast.textContent = message;
  elements.toast.classList.add("is-visible");
  state.toastTimer = setTimeout(() => elements.toast.classList.remove("is-visible"), 2800);
}

async function openSettings() {
  const [serverUrl, serverToken, savedVaultKey] = await Promise.all([
    getMeta("serverUrl", DEFAULT_SYNC_URL),
    getMeta("serverToken", ""),
    getMeta("vaultKey", ""),
  ]);
  elements.settingsError.textContent = "";
  elements.serverUrl.value = serverUrl;
  elements.serverToken.value = serverToken;
  elements.vaultKey.value = savedVaultKey || generateVaultKey();
  elements.vaultKey.type = "password";
  elements.revealVaultKey.textContent = "显示";
  elements.settingsOverlay.hidden = false;
  requestAnimationFrame(() => elements.serverUrl.focus({ preventScroll: true }));
  refreshUpdateStatus({ silent: true });
}

async function refreshUpdateStatus({ silent = false } = {}) {
  if (state.updateChecking) return state.availableUpdate;
  state.updateChecking = true;
  elements.checkUpdate.disabled = true;
  if (!silent) elements.updateStatus.textContent = "正在检查新版本…";

  try {
    const currentVersion = await getCurrentAppVersion();
    elements.currentVersion.textContent = `v${currentVersion}`;
    const result = await checkForAppUpdate();
    state.availableUpdate = result.available ? result : null;
    if (result.reason === "web") {
      elements.updateStatus.textContent = "网页版会自动更新；安装版可在这里检查更新。";
      elements.checkUpdate.hidden = true;
    } else if (result.available) {
      elements.updateStatus.textContent = result.notes
        ? `发现 v${result.version}：${result.notes}`
        : `发现新版本 v${result.version}`;
      elements.checkUpdate.textContent = `更新到 v${result.version}`;
    } else {
      elements.updateStatus.textContent = "当前已经是最新版本。";
      elements.checkUpdate.textContent = "再次检查";
    }
    return state.availableUpdate;
  } catch (error) {
    elements.updateStatus.textContent = error.message || "暂时无法检查更新。";
    elements.checkUpdate.textContent = "重新检查";
    state.availableUpdate = null;
    return null;
  } finally {
    elements.checkUpdate.disabled = false;
    state.updateChecking = false;
  }
}

async function handleUpdate() {
  if (!state.availableUpdate) {
    await refreshUpdateStatus();
    return;
  }

  elements.checkUpdate.disabled = true;
  elements.checkUpdate.textContent = "准备更新…";
  try {
    const result = await installAppUpdate(({ event, downloaded, total }) => {
      if (event === "Started") elements.updateStatus.textContent = "正在下载更新…";
      if (event === "Progress") {
        const percentage = total ? Math.min(100, Math.round((downloaded / total) * 100)) : null;
        elements.updateStatus.textContent = percentage
          ? `正在下载更新… ${percentage}%`
          : "正在下载更新…";
      }
      if (event === "Finished") elements.updateStatus.textContent = "下载完成，正在安装…";
    });
    if (result.requiresSystemConfirmation) {
      elements.updateStatus.textContent = "APK 已交给系统下载；完成后请按 OPPO 的提示确认安装。";
      elements.checkUpdate.textContent = "重新打开下载";
      elements.checkUpdate.disabled = false;
    }
  } catch (error) {
    elements.updateStatus.textContent = error.message || "更新失败，请稍后重试。";
    elements.checkUpdate.textContent = "重试更新";
    elements.checkUpdate.disabled = false;
  }
}

function closeSettings() {
  elements.settingsOverlay.hidden = true;
  elements.settingsError.textContent = "";
}

function normalizeServerUrl(value) {
  const trimmed = value.trim().replace(/\/+$/, "");
  if (!trimmed) return "";
  const url = new URL(trimmed);
  if (!["http:", "https:"].includes(url.protocol)) {
    throw new Error("同步地址必须以 https:// 开头");
  }
  return trimmed;
}

function isLoopbackUrl(value) {
  if (!value) return false;
  try {
    return /^(127(?:\.\d+){3}|localhost|\[?::1\]?)$/i.test(new URL(value).hostname);
  } catch {
    return false;
  }
}

function isSecureSyncUrl(value) {
  if (!value) return false;
  try {
    const url = new URL(value);
    if (url.protocol === "https:") return true;
    return url.protocol === "http:" && isLoopbackUrl(value);
  } catch {
    return false;
  }
}

async function saveSyncSettings() {
  elements.settingsError.textContent = "";
  elements.saveSettings.disabled = true;

  try {
    const [previousUrl, previousToken, previousVaultKey] = await Promise.all([
      getMeta("serverUrl", ""),
      getMeta("serverToken", ""),
      getMeta("vaultKey", ""),
    ]);
    let serverUrl = normalizeServerUrl(elements.serverUrl.value);
    const serverToken = elements.serverToken.value.trim();
    const vaultKey = normalizeVaultKey(elements.vaultKey.value);

    if (isNativeDatabase() && !serverUrl) {
      throw new Error("请填写云端同步地址");
    }
    if (isNativeDatabase() && !serverToken) {
      throw new Error("请填写访问令牌");
    }
    if (serverToken.length < 32) {
      throw new Error("访问令牌长度不足，请使用部署时生成的随机令牌");
    }
    if (!isSecureSyncUrl(serverUrl)) {
      throw new Error("云端同步必须使用正式 HTTPS 地址；只有本机开发可使用 http://127.0.0.1");
    }

    await Promise.all([
      setMeta("serverUrl", serverUrl),
      setMeta("serverToken", serverToken),
      setMeta("vaultKey", vaultKey),
    ]);

    state.cloudReady = isSecureSyncUrl(serverUrl);

    if (
      (previousUrl && previousUrl !== serverUrl) ||
      (previousToken && previousToken !== serverToken) ||
      (previousVaultKey && previousVaultKey !== vaultKey)
    ) {
      await setMeta("syncCursor", 0);
      for (const note of state.notes) {
        note.serverRevision = 0;
        note.syncedBody = null;
        await putNote(note);
        await queueNote(note);
      }
    }

    closeSettings();
    await performSync();
  } catch (error) {
    elements.settingsError.textContent = error.message || "无法保存同步设置";
  } finally {
    elements.saveSettings.disabled = false;
  }
}

async function createNote() {
  await flushPendingSave();
  if (state.view === "trash") setView("notes");
  const timestamp = Date.now();
  const note = {
    id: crypto.randomUUID(),
    body: "",
    createdAt: timestamp,
    updatedAt: timestamp,
    deletedAt: null,
    pinned: false,
    serverRevision: 0,
    syncedBody: null,
  };
  await putNote(note);
  await queueNote(note);
  state.notes.push(note);
  setLocalSaveStatus();
  selectNote(note.id, true);
  scheduleSync();
}

async function saveActiveToLocal() {
  clearTimeout(state.saveTimer);
  state.saveTimer = null;
  const note = activeNote();
  if (!note) return;
  await putNote({ ...note });
  await queueNote(note);
  renderList();
  renderEditor({ preserveCaret: true });
  setLocalSaveStatus();
  scheduleSync();
}

async function flushPendingSave() {
  if (!state.saveTimer) return;
  clearTimeout(state.saveTimer);
  state.saveTimer = null;
  await saveActiveToLocal();
}

function scheduleSave() {
  clearTimeout(state.saveTimer);
  state.saveTimer = setTimeout(() => saveActiveToLocal(), LOCAL_SAVE_DELAY);
}

function activeEditRemaining() {
  if (!state.lastInputAt) return 0;
  return Math.max(0, state.lastInputAt + EDIT_SYNC_IDLE_DELAY - Date.now());
}

function scheduleSync(delay = EDIT_SYNC_IDLE_DELAY) {
  clearTimeout(state.syncTimer);
  if (!navigator.onLine) return;
  const wait = Math.max(delay, activeEditRemaining());
  state.syncTimer = setTimeout(() => performSync({ quiet: true }), wait);
}

async function applyCompletedSyncPass(result, generationAtStart) {
  const syncedNotes = await getAllNotes();

  // A network response must never replace text typed while that request was
  // in flight. Keep the draft, but adopt the newest remote merge base so the
  // next pass can upload it without creating a false conflict.
  const editedDuringSync = state.editGeneration !== generationAtStart;
  const draft = editedDuringSync ? activeNote() : null;
  if (draft) {
    const syncedDraft = syncedNotes.find((note) => note.id === draft.id);
    if (syncedDraft) {
      const preservedDraft = {
        ...draft,
        deletedAt:
          draft.deletedAt || syncedDraft.deletedAt
            ? Math.max(draft.deletedAt || 0, syncedDraft.deletedAt || 0)
            : null,
        pinned: draft.deletedAt || syncedDraft.deletedAt ? false : draft.pinned,
        serverRevision: syncedDraft.serverRevision,
        syncedBody: syncedDraft.syncedBody,
      };
      await putNote(preservedDraft);
      await queueNote(preservedDraft);
      const index = syncedNotes.findIndex((note) => note.id === draft.id);
      syncedNotes[index] = preservedDraft;
    }
  }

  state.notes = syncedNotes;
  if (!state.notes.some((note) => note.id === state.activeId)) state.activeId = null;
  renderList();
  renderEditor({ preserveCaret: editedDuringSync });

  return {
    ...result,
    editedDuringSync,
    pending: (await getOutboxOperations()).length,
  };
}

async function performSync({ quiet = false, force = false } = {}) {
  clearTimeout(state.syncTimer);
  state.syncTimer = null;

  if (state.syncInProgress) {
    state.syncAgain = true;
    return;
  }
  if (!force && activeEditRemaining() > 0) {
    setLocalSaveStatus();
    scheduleSync(activeEditRemaining());
    return;
  }

  state.syncInProgress = true;
  try {
    await flushPendingSave();
    const pendingBeforeSync = await getOutboxOperations();

    if (!navigator.onLine) {
      setSyncStatus("offline", "离线，已保存本机");
      if (!quiet) showToast("当前离线；修改已经安全保存在本机。 ");
      return;
    }

    // A quiet pull with no local changes stays visually silent. A real upload
    // is one visible session even if a conflict/rebase needs several passes.
    if (!quiet || pendingBeforeSync.length) setSyncStatus("syncing", "正在同步…");

    const summary = await drainSyncSession({
      runPass: async () => {
        const generationAtStart = state.editGeneration;
        const result = await synchronize(state.deviceId);
        return applyCompletedSyncPass(result, generationAtStart);
      },
      shouldPause: (result) => result.editedDuringSync,
    });

    if (summary.conflicts) {
      showToast("另一台设备同时修改了同一段文字；当前内容未打断，另一个版本已保留。");
    } else if (!quiet && summary.merged) {
      showToast("已自动合并另一台设备的修改。");
    } else if (!quiet && (summary.pushed || summary.pulled)) {
      showToast("所有笔记已经同步。");
    }

    if (summary.paused) {
      setLocalSaveStatus();
      scheduleSync(Math.max(180, activeEditRemaining()));
    } else if (!summary.settled) {
      setSyncStatus("error", "同步暂未完成，修改已保存在本机");
      scheduleSync(2_000);
    } else if (state.cloudReady) {
      setSyncStatus("synced", summary.merged ? "已自动合并并安全同步" : "已安全同步");
    } else {
      setSyncStatus("local", "本机已保存；云端同步待配置");
    }
  } catch (error) {
    if (error instanceof SyncConfigurationError) {
      setSyncStatus("error", "需要设置云端同步");
      if (!quiet) await openSettings();
      return;
    }
    if (error instanceof SyncEncryptionError) {
      setSyncStatus("error", "加密密钥需要确认");
      if (!quiet) {
        showToast(error.message);
        await openSettings();
      }
      return;
    }
    console.warn("Sync unavailable", error);
    const pending = await getOutboxOperations();
    setSyncStatus("error", pending.length ? "主机暂时不可达，修改已保存在本机" : "主机暂时不可达");
    if (!quiet) showToast("暂时连接不到同步主机，不影响继续编辑。 ");
  } finally {
    state.syncInProgress = false;
    if (state.syncAgain) {
      state.syncAgain = false;
      const pending = await getOutboxOperations().catch(() => []);
      if (pending.length) scheduleSync(Math.max(180, activeEditRemaining()));
    }
  }
}

function setView(view) {
  state.view = view;
  elements.filterTabs.forEach((tab) => tab.classList.toggle("is-active", tab.dataset.view === view));
  const visible = filteredNotes();
  if (!visible.some((note) => note.id === state.activeId)) state.activeId = visible[0]?.id || null;
  renderList();
  renderEditor();
}

async function togglePin() {
  const note = activeNote();
  if (!note || note.deletedAt) return;
  note.pinned = !note.pinned;
  note.updatedAt = Date.now();
  await saveActiveToLocal();
}

async function toggleDeleted(noteId = state.activeId) {
  await flushPendingSave();
  const note = state.notes.find((candidate) => candidate.id === noteId);
  if (!note) return;
  const wasActive = state.activeId === noteId;
  const restoring = Boolean(note.deletedAt);
  note.deletedAt = restoring ? null : Date.now();
  if (!restoring) note.pinned = false;
  note.updatedAt = Date.now();
  await putNote({ ...note });
  await queueNote(note);
  state.openSwipeId = null;

  const visible = filteredNotes();
  if (!visible.some((candidate) => candidate.id === state.activeId)) {
    state.activeId = visible[0]?.id || null;
  }
  renderList();
  renderEditor();
  setLocalSaveStatus();
  scheduleSync(180);
  showToast(restoring ? "笔记已恢复。" : "已移到最近删除。 ");
  if (wasActive && mobileQuery.matches) returnToMobileList();
}

async function initialize() {
  state.deviceId = await getMeta("deviceId");
  if (!state.deviceId) {
    state.deviceId = crypto.randomUUID();
    await setMeta("deviceId", state.deviceId);
  }

  state.notes = await getAllNotes();
  state.activeId = filteredNotes()[0]?.id || null;
  renderList();
  renderEditor();
  setSyncStatus(navigator.onLine ? "local" : "offline", navigator.onLine ? "检查同步状态…" : "离线，已保存本机");

  const previewHost = ["localhost", "127.0.0.1"].includes(location.hostname) && location.port === "1420";
  if (!isNativeApp && "serviceWorker" in navigator && !previewHost) {
    navigator.serviceWorker.register("/service-worker.js").catch((error) => console.warn("Service worker", error));
  } else if (previewHost && "serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) registration.unregister();
    });
  }

  const serverUrl = await getMeta("serverUrl", "");
  state.cloudReady = isSecureSyncUrl(serverUrl);
  if (isNativeDatabase() && !serverUrl) {
    setSyncStatus("error", "需要设置云端同步");
    setTimeout(() => openSettings(), 350);
  } else if (navigator.onLine) {
    scheduleSync(250);
  }
}

elements.newNote.addEventListener("click", createNote);
elements.emptyNewNote.addEventListener("click", createNote);
elements.search.addEventListener("input", (event) => {
  state.search = event.target.value;
  renderList();
});
elements.filterTabs.forEach((tab) => tab.addEventListener("click", () => setView(tab.dataset.view)));
elements.editor.addEventListener("input", () => {
  const note = activeNote();
  if (!note) return;
  note.body = elements.editor.value;
  note.updatedAt = Date.now();
  state.lastInputAt = note.updatedAt;
  state.editGeneration += 1;
  elements.editedTime.textContent = "正在编辑";
  elements.wordCount.textContent = `${[...note.body].length} 字`;
  setLocalSaveStatus("正在编辑，本机自动保存");
  scheduleSave();
});
elements.pinNote.addEventListener("click", togglePin);
elements.deleteNote.addEventListener("click", () => toggleDeleted());
elements.notesList.addEventListener("pointerdown", (event) => {
  if (!event.target.closest(".note-row")) closeOpenSwipe();
});
elements.backButton.addEventListener("click", async () => {
  await flushPendingSave();
  returnToMobileList();
});
elements.syncButton.addEventListener("click", () => performSync({ force: true }));
elements.settingsButton.addEventListener("click", openSettings);
elements.closeSettings.addEventListener("click", closeSettings);
elements.cancelSettings.addEventListener("click", closeSettings);
elements.saveSettings.addEventListener("click", saveSyncSettings);
elements.generateVaultKey.addEventListener("click", () => {
  elements.vaultKey.value = generateVaultKey();
  elements.vaultKey.type = "text";
  elements.revealVaultKey.textContent = "隐藏";
  showToast("已生成新的加密密钥；请妥善保存。 ");
});
elements.revealVaultKey.addEventListener("click", () => {
  const revealing = elements.vaultKey.type === "password";
  elements.vaultKey.type = revealing ? "text" : "password";
  elements.revealVaultKey.textContent = revealing ? "隐藏" : "显示";
});
elements.copyVaultKey.addEventListener("click", async () => {
  const value = elements.vaultKey.value.trim();
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    showToast("加密密钥已复制。 ");
  } catch {
    elements.vaultKey.type = "text";
    elements.vaultKey.select();
    showToast("请复制已经选中的加密密钥。 ");
  }
});
elements.checkUpdate.addEventListener("click", handleUpdate);
elements.settingsOverlay.addEventListener("click", (event) => {
  if (event.target === elements.settingsOverlay) closeSettings();
});

window.addEventListener("online", () => {
  setSyncStatus("local", "网络已恢复");
  scheduleSync(200);
});
window.addEventListener("offline", () => setSyncStatus("offline", "离线，已保存本机"));
window.addEventListener("popstate", (event) => {
  if (!mobileQuery.matches) return;

  const historyView = event.state?.[MOBILE_HISTORY_STATE_KEY];
  const noteId = event.state?.lightBoatNoteId;
  if (historyView === MOBILE_EDITOR_VIEW && state.notes.some((note) => note.id === noteId)) {
    state.activeId = noteId;
    renderList();
    renderEditor();
    document.body.classList.add("editor-open");
    return;
  }

  closeMobileEditor();
  void flushPendingSave();
});
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") scheduleSync(250);
});
window.setInterval(() => {
  if (navigator.onLine && document.visibilityState === "visible" && elements.settingsOverlay.hidden) {
    performSync({ quiet: true });
  }
}, PERIODIC_SYNC_INTERVAL);
window.addEventListener("beforeunload", () => {
  const note = activeNote();
  if (note && state.saveTimer) putNote({ ...note });
});
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && !elements.settingsOverlay.hidden) {
    closeSettings();
    return;
  }
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "n") {
    event.preventDefault();
    createNote();
  }
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
    event.preventDefault();
    returnToMobileList();
    elements.search.focus();
  }
});

initialize().catch((error) => {
  console.error(error);
  setSyncStatus("error", "本地数据库启动失败");
  showToast("无法打开本地数据库，请重新加载应用。 ");
});
